/**
 * /app/$tenantSlug/payroll/tax-declaration/$declId — HR per-employee verify
 * view. Each item: declared amount, proof viewer, verify / partial / reject;
 * sticky Finalise footer (step-up TOTP), enabled once all items are decided.
 *
 * RBAC: company_admin / hr_manager / accountant (backend boundary). Finalise
 * is step-up TOTP gated (totp_required → redirect to /2fa-verify; mirrors the
 * payroll lock-dialog).
 */

import { useState } from "react";
import {
  createFileRoute,
  Link,
  useNavigate,
  useLocation,
  useParams,
} from "@tanstack/react-router";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { isAccountingAdmin } from "@/lib/nav";
import {
  useDeclarationForReview,
  useEffectiveDeductions,
  useFinaliseDeclaration,
  useVerifyItem,
} from "@/hooks/use-tax-declaration";
import { useDirectory } from "@/hooks/use-directory";
import * as taxApi from "@/lib/api/tax-declaration";
import type { DeclarationItem } from "@/types/tax-declaration";
import { PageHeader } from "@/components/shared/page-header";
import { StatusBadge } from "@/components/shared/status-badge";
import { GlossaryTerm } from "@/components/shared/glossary-term";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { formatPaise, rupeesToPaise } from "@/lib/expense-format";
import { fyLabel } from "@/lib/indian-fy";
import { toApiError } from "@/lib/api/client";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/payroll/tax-declaration/$declId",
)({
  component: DeclarationReviewRoute,
});

function DeclarationReviewRoute() {
  const { tenantSlug, declId } = useParams({ strict: false }) as {
    tenantSlug: string;
    declId: string;
  };
  const id = Number(declId);
  const { user } = useAuth();
  const navigate = useNavigate();
  const pathname = useLocation({ select: (l) => l.pathname });
  const { data, isLoading } = useDeclarationForReview(id, isAccountingAdmin(user?.role));
  const { data: effective } = useEffectiveDeductions(id, isAccountingAdmin(user?.role));
  const finalise = useFinaliseDeclaration(id);
  const dir = useDirectory({ includeInactive: true });

  if (!isAccountingAdmin(user?.role)) {
    return (
      <>
        <PageHeader title="Tax Declaration" description="Verification." />
        <Card>
          <CardContent className="pt-6 text-sm text-muted-foreground">
            You do not have access to verify tax declarations.
          </CardContent>
        </Card>
      </>
    );
  }

  const allDecided =
    data?.items.every(
      (i) =>
        i.item_status === "verified" ||
        i.item_status === "partially_verified" ||
        i.item_status === "rejected",
    ) ?? false;
  // HR may also close the proof window of an employee who never submitted
  // proofs (proofs_open): unproven items then count 0 for TDS.
  const finalisable = data?.status === "under_review" || data?.status === "proofs_open";
  const canFinalise = finalisable && allDecided;

  function doFinalise() {
    finalise.mutate(undefined, {
      onSuccess: () => toast.success("Declaration finalised — verified amounts now drive TDS."),
      onError: (err) => {
        const e = toApiError(err);
        if (e.error === "totp_enrollment_required") {
          toast.error("Enrol in two-factor authentication to finalise.");
          void navigate({ to: "/2fa-setup", search: { required: 1, next: pathname } });
          return;
        }
        if (e.error === "totp_required") {
          toast.error("Two-factor verification required to finalise.");
          void navigate({ to: "/2fa-verify", search: { next: pathname } });
          return;
        }
        toast.error(e.message);
      },
    });
  }

  return (
    <>
      <PageHeader
        // The person and year name the page (breadcrumb, tab title).
        title={
          data
            ? `Tax Declaration — ${dir.label(data.employee_id, null, false)}, ${fyLabel(data.financial_year)}`
            : `Declaration #${declId}`
        }
        description="Verify each declared item against its proof, then finalise."
      />
      {/* Phones only: the desktop breadcrumb goes back to the queue. */}
      <div className="mb-4 lg:hidden">
        <Button asChild variant="outline" size="sm" className="gap-1.5">
          <Link to="/app/$tenantSlug/payroll/tax-declaration/queue" params={{ tenantSlug }}>
            <ArrowLeft className="h-4 w-4" /> Back to Queue
          </Link>
        </Button>
      </div>

      {isLoading && (
        <div className="space-y-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      )}

      {data && (
        <div className="space-y-4 pb-4">
          <div className="flex items-center gap-2">
            <StatusBadge status={data.status} />
            <span className="text-sm text-muted-foreground">
              {dir.label(data.employee_id)} · {fyLabel(data.financial_year)}
            </span>
          </div>

          {data.items.map((item) => (
            <VerifyRow key={item.id} declId={id} item={item} readOnly={data.status === "finalised"} />
          ))}

          {effective && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-1.5 text-base">
                  Effective Deductions ({effective.source}, {effective.regime} regime)
                  <GlossaryTerm
                    term={effective.regime === "old" ? "OldRegime" : "NewRegime"}
                    className="-mx-0.5 -my-1.5 size-7 shrink-0 justify-center pointer-coarse:-m-[13px] pointer-coarse:size-11"
                  />
                </CardTitle>
              </CardHeader>
              <CardContent className="grid gap-1 text-sm sm:grid-cols-2 pointer-coarse:gap-y-6">
                <span>Standard deduction: {formatPaise(effective.standard_deduction)}</span>
                <span className="inline-flex items-center gap-1">
                  §80C
                  <GlossaryTerm term="Sec80C" />: {formatPaise(effective.sec_80c)}
                </span>
                <span className="inline-flex items-center gap-1">
                  §80CCD(1B)
                  <GlossaryTerm term="Sec80CCD1B" />: {formatPaise(effective.sec_80ccd_1b)}
                </span>
                <span className="inline-flex items-center gap-1">
                  §80D
                  <GlossaryTerm term="Sec80D" />: {formatPaise(effective.sec_80d)}
                </span>
                <span>HRA exempt: {formatPaise(effective.hra_exempt)}</span>
                {(effective.lta_claimed ?? 0) > 0 && (
                  <span>
                    LTA exempt: {formatPaise(effective.lta)} of {formatPaise(effective.lta_claimed ?? 0)} declared —
                    §10(5) exempts only the leave travel allowance paid through payroll (
                    {formatPaise(effective.lta_received ?? 0)} paid this year; pay LTA as the LTA earning on a payroll run)
                  </span>
                )}
                {(effective.interest_income ?? 0) > 0 && (
                  <span>
                    §80TTA / 80TTB: {formatPaise(effective.sec_80tta_ttb)} on interest income of{" "}
                    {formatPaise(effective.interest_income ?? 0)} (counted as income for TDS)
                  </span>
                )}
                {effective.hra_needs_reverification && (
                  <span className="text-amber-800 sm:col-span-2">
                    The HRA item was verified before HR verified the rent itself; the exemption uses the share of the
                    declared rent that was accepted. Re-verify it as the annual rent paid.
                  </span>
                )}
                <span>Home-loan interest: {formatPaise(effective.home_loan_interest)}</span>
                <span className="font-medium">
                  Total Chapter-VI-A: {formatPaise(effective.total_chapter_via)}
                </span>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {data && finalisable && (
        <div className="sticky bottom-[var(--bottom-bar-h,0px)] z-30 -mx-4 border-t bg-background/95 p-4 backdrop-blur sm:mx-0 sm:rounded-lg sm:border">
          <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3">
            <span className="min-w-0 flex-1 text-sm text-muted-foreground">
              {data.status === "proofs_open" && "The employee has not submitted proofs yet. "}
              {allDecided
                ? "All items decided — ready to finalise."
                : data.status === "proofs_open"
                  ? "Decide every item (reject any without proof), then finalise to close the proof window."
                  : "Decide every item before finalising."}
            </span>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button disabled={!canFinalise || finalise.isPending}>
                  {finalise.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Finalise (2FA)
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="max-w-lg">
                <AlertDialogHeader>
                  <AlertDialogTitle>Finalise this declaration?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Finalising locks every verify / reject decision below. The
                    locked amounts become immutable and drive TDS — this cannot
                    be undone. Two-factor verification is required.
                  </AlertDialogDescription>
                </AlertDialogHeader>

                {/* Read-only preview of exactly what will be locked. */}
                <div className="max-h-72 space-y-1.5 overflow-y-auto rounded-md border p-3 text-sm">
                  {data.items.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between gap-2"
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        <span className="truncate">{item.head}</span>
                        <StatusBadge status={item.item_status} />
                      </span>
                      <span className="shrink-0 tabular-nums">
                        {formatPaise(
                          item.verified_paise != null
                            ? item.verified_paise
                            : item.declared_paise,
                        )}
                      </span>
                    </div>
                  ))}
                  {effective && (
                    <div className="mt-1.5 flex items-center justify-between gap-2 border-t pt-1.5 font-medium">
                      <span>Effective total Chapter-VI-A</span>
                      <span className="shrink-0 tabular-nums">
                        {formatPaise(effective.total_chapter_via)}
                      </span>
                    </div>
                  )}
                </div>

                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={doFinalise}
                    disabled={!canFinalise || finalise.isPending}
                  >
                    Finalise (2FA)
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      )}
    </>
  );
}

function VerifyRow({
  declId,
  item,
  readOnly,
}: {
  declId: number;
  item: DeclarationItem;
  readOnly: boolean;
}) {
  const verify = useVerifyItem(declId);
  const [amount, setAmount] = useState<string>("");
  const [reason, setReason] = useState<string>("");
  const locked = readOnly || item.item_status === "verified";

  function decide(
    status: "verified" | "partially_verified" | "rejected",
  ) {
    if (status === "rejected") {
      if (!reason.trim()) {
        toast.error("A rejection reason is required.");
        return;
      }
      verify.mutate(
        { itemId: item.id, body: { item_status: "rejected", rejection_reason: reason.trim() } },
        {
          onSuccess: () => toast.success("Item rejected"),
          onError: (e) => toast.error(toApiError(e).message),
        },
      );
      return;
    }
    // HRA: HR verifies the ANNUAL RENT (what Part B and, after
    // finalisation, TDS use) — it defaults to the declared rent.
    const declared =
      item.head === "HRA" && item.hra_annual_rent_paise != null
        ? item.hra_annual_rent_paise
        : item.declared_paise;
    const paise =
      status === "verified" && amount.trim() === ""
        ? declared
        : rupeesToPaise(amount);
    if (paise == null) {
      toast.error("Invalid verified amount.");
      return;
    }
    verify.mutate(
      { itemId: item.id, body: { item_status: status, verified_paise: paise } },
      {
        onSuccess: () => toast.success("Item verified"),
        onError: (e) => {
          const err = toApiError(e);
          toast.error(
            err.error === "cap_exceeded"
              ? "Verified amount exceeds the statutory cap for this head."
              : err.message,
          );
        },
      },
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between text-base">
          <span>{item.head}</span>
          <StatusBadge status={item.item_status} />
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm">
          Declared: <strong>{formatPaise(item.declared_paise)}</strong>
          {item.verified_paise != null && (
            <>
              {" · "}Verified: <strong>{formatPaise(item.verified_paise)}</strong>
            </>
          )}
        </p>
        {item.hra_needs_reverification && (
          <p className="text-xs text-amber-800" data-testid={`decl-item-${item.id}-hra-reverify`}>
            Verified before HR verified the rent itself — the exemption uses the share of the declared rent that was
            accepted. Re-verify this item as the annual rent paid.
          </p>
        )}
        {item.hra_landlord_pan_masked && (
          <p className="text-xs text-muted-foreground">
            Landlord PAN: {item.hra_landlord_pan_masked}
            {item.hra_annual_rent_paise != null && (
              <> · Annual rent {formatPaise(item.hra_annual_rent_paise)}</>
            )}
          </p>
        )}

        {/* Proofs */}
        <div className="flex flex-wrap gap-2">
          {item.proofs.map((p) => (
            <Button
              key={p.id}
              size="sm"
              variant="outline"
              onClick={async () => {
                try {
                  const blob = await taxApi.downloadProofForReview(declId, item.id, p.id);
                  const url = URL.createObjectURL(blob);
                  window.open(url, "_blank", "noopener");
                  setTimeout(() => URL.revokeObjectURL(url), 60_000);
                } catch (e) {
                  toast.error(toApiError(e).message);
                }
              }}
            >
              View {p.file_name}
            </Button>
          ))}
          {item.proofs.length === 0 && (
            <span className="text-sm text-muted-foreground">No proofs uploaded.</span>
          )}
        </div>

        {!locked && (
          <div className="space-y-2 border-t pt-3">
            <div className="flex flex-wrap items-center gap-2">
              <Input
                inputMode="decimal"
                placeholder={
                  item.head === "HRA" && item.hra_annual_rent_paise != null
                    ? "Verified annual rent (₹) — defaults to the declared rent"
                    : "Verified amount (₹) — defaults to declared"
                }
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="max-w-xs"
              />
              <Button variant="outline" size="sm" onClick={() => decide("verified")} disabled={verify.isPending}>
                Verify
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => decide("partially_verified")}
                disabled={verify.isPending}
              >
                Partial
              </Button>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Textarea
                placeholder="Rejection reason (required to reject)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                className="max-w-md"
              />
              <Button
                size="sm"
                variant="destructive"
                onClick={() => decide("rejected")}
                disabled={verify.isPending}
              >
                Reject
              </Button>
            </div>
          </div>
        )}
        {item.rejection_reason && (
          <p className="text-sm text-destructive">Rejected: {item.rejection_reason}</p>
        )}
      </CardContent>
    </Card>
  );
}
